import { createAsyncThunk } from '@reduxjs/toolkit';

import { registrarAcao } from '@/shared/utils/reportAction';

import type {
  ErroDaSimulacao,
  AssinaturaSimulada,
  Config,
  ResultadoDaAssinatura,
} from '../../types';
import { ehAssinaturaSimulada, podeRegistrarResultado } from '../../utils/assinaturaSimulada';
import { desfechoDesconhecido, intencaoReaproveitavel } from '../../utils/intencaoDeComando';
import { intencaoRegistrada, registroDaAssinaturaFinalizado } from '../eventosDoStore';
import { paraErroDaSimulacao, registrarFalha } from '../falhasDaSimulacao';
import { selectEstadoDaSimulacao as estadoDaFeature } from '../selectors';
import { buscarConfiguracaoDoAdmin } from '../admin/actions';
import { relerSeConfiguracaoAlterada } from '../ciclo/actions';
import { servicosDaSimulacao } from '../servicosDaSimulacao';
import { FalhaDaSimulacao } from '../services';

/** Sem simulação atual, nenhum link de assinatura vale: é o mesmo 404 de um link de outra conta. */
const SEM_SIMULACAO_ATUAL: ErroDaSimulacao = {
  status: 404,
  codigo: 'SIMULACAO_NAO_ENCONTRADA',
  mensagem: 'Esta simulação não está mais disponível. Inicie uma nova.',
  camposInvalidos: [],
  comandoId: null,
};

/**
 * Abre a assinatura de teste (TELA-002) pela simulação atual, relida a cada abertura: o link só
 * vale para a execução ativa da conta, e o core responde 404 para o de outra execução ou conta.
 * Recarregar a página só lê; o resultado sai apenas do clique (CA-19).
 */
export const abrirAssinaturaSimulada = createAsyncThunk<
  { simulacaoId: string; assinatura: AssinaturaSimulada },
  string,
  Config
>(
  'negociacaoSimulacao/abrirAssinatura',
  async (assinaturaId, { dispatch, rejectWithValue, getState }) => {
    let simulacaoId: string | null = null;
    const { escritorioId, contextoAdmin, configuracaoAdmin, superficie, marcoDaSimulacao } =
      estadoDaFeature(getState());
    try {
      // Um link aberto diretamente não passou pela tela de conversa para identificar o agente.
      if (superficie === 'ADMIN' && escritorioId && contextoAdmin && !configuracaoAdmin.dados) {
        const leitura = await dispatch(
          buscarConfiguracaoDoAdmin({ flowId: contextoAdmin.flowId, escritorioId }),
        );
        if (!buscarConfiguracaoDoAdmin.fulfilled.match(leitura))
          throw new FalhaDaSimulacao(leitura.payload ?? SEM_SIMULACAO_ATUAL);
      }
      if (estadoDaFeature(getState()).marcoDaSimulacao !== marcoDaSimulacao)
        return rejectWithValue(SEM_SIMULACAO_ATUAL);
      const servicos = servicosDaSimulacao(getState());
      const atual = await servicos.buscarAtual();
      if (estadoDaFeature(getState()).escritorioId !== escritorioId)
        return rejectWithValue(SEM_SIMULACAO_ATUAL);
      if (!atual) throw new FalhaDaSimulacao(SEM_SIMULACAO_ATUAL);
      simulacaoId = atual.id;
      const assinatura = await servicos.buscarAssinatura(atual.id, assinaturaId);
      return { simulacaoId: atual.id, assinatura };
    } catch (erro) {
      const falha = paraErroDaSimulacao(erro);
      registrarFalha('abrirAssinatura', erro, falha, { simulacaoId, assinaturaId });
      return rejectWithValue(falha);
    }
  },
  { condition: (_, { getState }) => !estadoDaFeature(getState()).trocandoEscritorio },
);

/**
 * Registra o resultado da etapa do provedor uma vez só. Repetir a mesma ação depois de um
 * desfecho desconhecido (rede, 5xx) é a mesma intenção e sai com a mesma chave: o core devolve o
 * que já gravou em vez de republicar (CA-19). O `condition` barra o clique duplo e a ação que o
 * core não permite.
 */
export const registrarResultadoDaAssinatura = createAsyncThunk<
  AssinaturaSimulada,
  ResultadoDaAssinatura,
  Config
>(
  'negociacaoSimulacao/registrarResultadoDaAssinatura',
  async (resultado, { dispatch, getState, rejectWithValue, requestId }) => {
    const { assinatura, intencoes, escritorioId } = estadoDaFeature(getState());
    const simulacaoId = assinatura.simulacaoId ?? '';
    const assinaturaId = assinatura.dados?.id ?? '';
    const intencao = intencaoReaproveitavel(intencoes, 'registrarResultadoDaAssinatura', {
      simulacaoId,
      assinaturaId,
      resultado,
    });
    dispatch(
      intencaoRegistrada({
        tipo: 'registrarResultadoDaAssinatura',
        intencao: { ...intencao, requisicaoVigente: requestId },
      }),
    );
    const servicos = servicosDaSimulacao(getState());
    let desfechoConhecido = false;
    try {
      const registrada = await servicos.registrarResultadoDaAssinatura(
        simulacaoId,
        assinaturaId,
        resultado,
        intencao.chave,
      );
      if (estadoDaFeature(getState()).escritorioId !== escritorioId)
        return rejectWithValue(SEM_SIMULACAO_ATUAL);
      registrarAcao('negociacaoSimulacao.assinaturaRegistrada', {
        simulacaoId,
        assinaturaId,
        resultado,
      });
      const assinaturaRegistrada = ehAssinaturaSimulada(registrada)
        ? registrada
        : await servicos.buscarAssinatura(simulacaoId, assinaturaId);
      desfechoConhecido = true;
      return assinaturaRegistrada;
    } catch (erro) {
      const falha = paraErroDaSimulacao(erro);
      desfechoConhecido = !desfechoDesconhecido(falha);
      registrarFalha('registrarResultadoDaAssinatura', erro, falha, { simulacaoId, assinaturaId });
      if (estadoDaFeature(getState()).escritorioId !== escritorioId) return rejectWithValue(falha);
      relerSeConfiguracaoAlterada(falha, simulacaoId, dispatch);
      // Outra aba ou o clique anterior já decidiu: a tela mostra o resultado que ficou gravado.
      if (falha.codigo === 'ASSINATURA_JA_DECIDIDA') {
        const gravada = await servicos
          .buscarAssinatura(simulacaoId, assinaturaId)
          .catch(() => null);
        if (gravada) return gravada;
      }
      return rejectWithValue(falha);
    } finally {
      if (desfechoConhecido) dispatch(registroDaAssinaturaFinalizado(requestId));
    }
  },
  {
    condition: (resultado, { getState }) =>
      podeRegistrarResultado(estadoDaFeature(getState()).assinatura, resultado),
  },
);
